// exportAdvancedSearchToExcel.js
// Build an Excel workbook from advanced search results using exceljs

import { parseReportSections } from './parseReportSections';
import { getReportContent } from './reportListUtils';

/**
 * Export advanced search results to an Excel Blob.
 * Columns: Date | Template | <selected section titles...>
 * Each report is parsed using parseReportSections; section contents are placed in corresponding columns.
 *
 * @param {Array<Object>} reports - List of report objects
 * @param {Array<string>} selectedSectionTitles - Section titles to include as columns
 * @returns {Promise<Blob>} Excel file blob
 */
export default async function exportAdvancedSearchToExcel(reports = [], selectedSectionTitles = [], options = {}) {
  const { fullTextMode = false, extractConfig = {}, numbersConfig = {}, numbersModeConfig = {}, extractBirthDate = false, includeStatistics = false, exportAsCSV = false } = options;
  if (exportAsCSV) {
    // CSV path mirrors Excel layout and features
    const escapeCSV = (str) => {
      const s = (str ?? '').toString();
      if (/["]/.test(s) || /[\n\r]/.test(s) || s.includes(';') || s.includes(',')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const delimiter = ';';

    // Helper functions (CSV-local)
    const parseNumericValueCSV = (raw) => {
      if (raw === null || raw === undefined) return null;
      let s = String(raw).trim();
      if (!s) return null;
      s = s.replace(/[^0-9,.-]+/g, '');
      const lastComma = s.lastIndexOf(',');
      const lastDot = s.lastIndexOf('.');
      if (lastComma > -1 && lastDot > -1) {
        if (lastComma > lastDot) { s = s.replace(/\./g, ''); s = s.replace(/,/g, '.'); }
        else { s = s.replace(/,/g, ''); }
      } else if (lastComma > -1) { s = s.replace(/,/g, '.'); }
      const num = parseFloat(s);
      return Number.isFinite(num) ? num : null;
    };
    const extractOldestBirthDateCSV = (text, reportDate) => {
      if (!text) return null;
      const results = [];
      const pattern = /(\b\d{1,2})[./](\d{1,2})[./](\d{2,4})\b/g;
      let m; while ((m = pattern.exec(text)) !== null) {
        const d = parseInt(m[1], 10), mo = parseInt(m[2], 10); let y = m[3];
        if (!d || !mo || d>31 || mo>12) continue;
        if (y.length === 2) { const yy = parseInt(y,10); const cYY = new Date().getFullYear()%100; y = (yy>cYY? 1900+yy : 2000+yy); } else { y = parseInt(y,10); }
        if (!y || y<1900 || y>new Date().getFullYear()) continue; const dt = new Date(y, mo-1, d); if (!isNaN(dt)) results.push(dt);
      }
      if (results.length === 0) return null;
      results.sort((a,b)=>a-b); const oldest = results[0];
      if (reportDate instanceof Date && !isNaN(reportDate.getTime())) {
        const sameDay = oldest.toDateString() === reportDate.toDateString();
        if (sameDay) return null;
      }
      return oldest;
    };
    const computeAgeYearsCSV = (dob, atDate) => {
      if (!(dob instanceof Date) || !(atDate instanceof Date)) return '';
      let age = atDate.getFullYear() - dob.getFullYear();
      const m = atDate.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && atDate.getDate() < dob.getDate())) age--;
      return age >= 0 ? age : '';
    };

    // Build headers base
    let headers = ['Date', 'Template'];
    if (extractBirthDate) {
      headers.push('Date de naissance');
      headers.push('Âge');
    }

    // Discover inner titles when needed
    const needExtract = (title) => !!extractConfig && !!extractConfig[title];
    const innerTitlesByBase = {};
    if (!fullTextMode) {
      selectedSectionTitles.forEach((base) => { if (needExtract(base)) innerTitlesByBase[base] = []; });
      if (Object.keys(innerTitlesByBase).length > 0) {
        for (const rep of (reports || [])) {
          const content = getReportContent(rep) || '';
          const secs = parseReportSections(content);
          const baseMap = new Map();
          secs.forEach((sec)=>{
            const t=(sec?.title||'').trim(); const v=(sec?.content||'').trim(); if(!t) return; const prev=baseMap.get(t)||''; baseMap.set(t, prev&&v? `${prev}\n\n${v}`:(prev||v));
          });
          for (const base of Object.keys(innerTitlesByBase)) {
            const baseContent = baseMap.get(base) || '';
            if (!baseContent) continue;
            const inner = parseReportSections(baseContent);
            inner.forEach((s)=>{ const it=(s?.title||'').trim(); if(!it) return; const arr=innerTitlesByBase[base]; if(!arr.includes(it)) arr.push(it); });
          }
        }
      }
      Object.keys(innerTitlesByBase).forEach((b)=>{ innerTitlesByBase[b]= (innerTitlesByBase[b]||[]).sort((a,b2)=>a.localeCompare(b2)); });
      selectedSectionTitles.forEach((base)=>{
        headers.push(base);
        const innerList = innerTitlesByBase[base] || [];
        innerList.forEach((it)=> headers.push(it));
      });
      if (includeStatistics) headers.unshift('statistic');
    } else {
      if (includeStatistics) headers.unshift('statistic');
      headers.push('Contenu');
    }

    const rows = [];
    const numericColumnsValues = new Map();
    for (const rep of (reports||[])) {
      const createdAt = rep?.createdAt ? new Date(rep.createdAt) : null;
      const dateStr = createdAt ? createdAt.toLocaleString('fr-FR') : '';
      const template = rep?.reportType || '';
      const content = getReportContent(rep) || '';
      if (fullTextMode) {
        const row = [dateStr, template];
        if (extractBirthDate) {
          const dob = extractOldestBirthDateCSV(content, createdAt);
          row.push(dob ? dob.toLocaleDateString('fr-CH') : '');
          row.push(dob ? computeAgeYearsCSV(dob, createdAt) : '');
        }
        row.push(content);
        if (includeStatistics) row.unshift('');
        rows.push(row);
      } else {
        const secs = parseReportSections(content);
        const titleToContent = new Map();
        secs.forEach((sec)=>{ const t=(sec?.title||'').trim(); const v=(sec?.content||'').trim(); if(!t) return; const prev=titleToContent.get(t)||''; titleToContent.set(t, prev&&v? `${prev}\n\n${v}`:(prev||v)); });
        const row = [dateStr, template];
        if (extractBirthDate) {
          const dob = extractOldestBirthDateCSV(content, createdAt);
          row.push(dob ? dob.toLocaleDateString('fr-CH') : '');
          row.push(dob ? computeAgeYearsCSV(dob, createdAt) : '');
        }
        const rowValues = [];
        selectedSectionTitles.forEach((base)=>{
          let baseVal = titleToContent.get(base) || '';
          const hasInner = needExtract(base) && (innerTitlesByBase[base]||[]).length>0;
          if (numbersConfig[base] && !hasInner) baseVal = (baseVal||'').toString().replace(/[^\d.,-]+/g,'').trim();
          rowValues.push(baseVal);
          if (includeStatistics && numbersConfig[base] && !hasInner) {
            const n = (baseVal && baseVal.match) ? baseVal.match(/[-+]?[0-9]*[\.,]?[0-9]+/g) : null;
            if (n) {
              const parsed = n.map(s=> parseFloat(String(s).replace(/,/g,'.'))).filter(v=>Number.isFinite(v));
              const baseColIndex = headers.indexOf(base) + 1;
              if (baseColIndex>0) { if(!numericColumnsValues.has(baseColIndex)) numericColumnsValues.set(baseColIndex, []); numericColumnsValues.get(baseColIndex).push(...parsed); }
            }
          }
          if (needExtract(base)) {
            const innerList = innerTitlesByBase[base] || [];
            if (innerList.length>0) {
              const innerMap = new Map();
              parseReportSections(baseVal).forEach((s)=>{ const it=(s?.title||'').trim(); const iv=(s?.content||'').trim(); if(!it) return; const prev=innerMap.get(it)||''; innerMap.set(it, prev&&iv? `${prev}\n\n${iv}`:(prev||iv)); });
              innerList.forEach((it)=>{
                let v = innerMap.get(it) || '';
                if (numbersConfig[base]) v = (v||'').toString().replace(/[^\d.,-]+/g,'').trim();
                rowValues.push(v);
                if (includeStatistics && numbersConfig[base]) {
                  const n = (v && v.match) ? v.match(/[-+]?[0-9]*[\.,]?[0-9]+/g) : null;
                  if (n) {
                    const parsed = n.map(s=> parseFloat(String(s).replace(/,/g,'.'))).filter(vv=>Number.isFinite(vv));
                    const innerColIndex = headers.indexOf(it) + 1;
                    if (innerColIndex>0) { if(!numericColumnsValues.has(innerColIndex)) numericColumnsValues.set(innerColIndex, []); numericColumnsValues.get(innerColIndex).push(...parsed); }
                  }
                }
              });
            }
          }
        });
        row.push(...rowValues);
        if (includeStatistics) row.unshift('');
        rows.push(row);
      }
    }
    if (includeStatistics && !fullTextMode) {
      const metrics = [
        { name: 'mean', fn: (arr) => arr.reduce((a,b)=>a+b,0)/arr.length },
        { name: 'SD', fn: (arr) => { if (arr.length<2) return 0; const m = arr.reduce((a,b)=>a+b,0)/arr.length; return Math.sqrt(arr.reduce((s,v)=>s+Math.pow(v-m,2),0)/(arr.length-1)); }},
        { name: 'median', fn: (arr) => { const s=[...arr].sort((a,b)=>a-b); return s.length%2? s[(s.length-1)/2] : (s[s.length/2-1]+s[s.length/2])/2; }},
        { name: 'IQR 25', fn: (arr) => { const s=[...arr].sort((a,b)=>a-b); const pos=(s.length-1)*0.25; const b=Math.floor(pos); const r=pos-b; return s[b+1]!==undefined? s[b]+r*(s[b+1]-s[b]) : s[b]; }},
        { name: 'IQR 75', fn: (arr) => { const s=[...arr].sort((a,b)=>a-b); const pos=(s.length-1)*0.75; const b=Math.floor(pos); const r=pos-b; return s[b+1]!==undefined? s[b]+r*(s[b+1]-s[b]) : s[b]; }},
      ];
      const colIndices = Array.from(numericColumnsValues.keys()).sort((a,b)=>a-b);
      for (const metric of metrics) {
        const statsRow = new Array(headers.length).fill('');
        statsRow[0] = metric.name;
        for (const ci of colIndices) {
          const arr = numericColumnsValues.get(ci) || [];
          if (arr.length>0) statsRow[ci-1] = metric.fn(arr);
        }
        rows.unshift(statsRow);
      }
    }
    const csv = [headers.map(escapeCSV).join(delimiter), ...rows.map((r)=> r.map(escapeCSV).join(delimiter))].join('\r\n');
    return new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
  }
  const ExcelJSModule = await import('exceljs');
  const ExcelJS = ExcelJSModule.default || ExcelJSModule;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Rapports');

  // Build headers, supporting optional extraction per section
  let headers = ['Date', 'Template'];
  let columnIsNumeric = [false, false];
  let expandedInnerByBase = {};
  let birthDateColumnIndex = -1;
  let ageColumnIndex = -1;
  const fromNumbersExtraction = [false, false];
  if (extractBirthDate) {
    headers.push('Date de naissance');
    columnIsNumeric.push(false);
    fromNumbersExtraction.push(false);
    // Will set numFmt as a date per-row
    birthDateColumnIndex = headers.length; // 1-based later when applying
    headers.push('Âge');
    columnIsNumeric.push(false); // do not include age in numeric-extraction stats
    fromNumbersExtraction.push(false);
    ageColumnIndex = headers.length;
  }
  if (fullTextMode) {
    headers.push('Contenu');
    columnIsNumeric.push(false);
  } else {
    // Precompute inner titles for sections flagged as extract
    const needExtract = (title) => !!extractConfig && !!extractConfig[title];
    const innerTitlesByBase = {};
    selectedSectionTitles.forEach((base) => {
      if (needExtract(base)) innerTitlesByBase[base] = [];
    });

    if (Object.keys(innerTitlesByBase).length > 0) {
      for (const report of (reports || [])) {
        const content = getReportContent(report) || '';
        const sections = parseReportSections(content);
        const baseMap = new Map();
        for (const sec of sections) {
          const t = (sec?.title || '').trim();
          if (!t) continue;
          // merge contents
          const prev = baseMap.get(t) || '';
          const val = (sec?.content || '').trim();
          baseMap.set(t, prev && val ? `${prev}\n\n${val}` : (prev || val));
        }
        for (const base of Object.keys(innerTitlesByBase)) {
          const baseContent = baseMap.get(base) || '';
          if (!baseContent) continue;
          const inner = parseReportSections(baseContent);
          inner.forEach((s) => {
            const it = (s?.title || '').trim();
            if (!it) return;
            const arr = innerTitlesByBase[base];
            if (!arr.includes(it)) arr.push(it);
          });
        }
      }
    }

    // Sort inner titles alphabetically for stable column order
    Object.keys(innerTitlesByBase).forEach((b) => {
      innerTitlesByBase[b] = (innerTitlesByBase[b] || []).sort((a, b2) => a.localeCompare(b2));
    });

    expandedInnerByBase = innerTitlesByBase;

    // Compose final headers
    selectedSectionTitles.forEach((base) => {
      const innerList = needExtract(base) ? (expandedInnerByBase[base] || []) : [];
      // Always include main section column
      headers.push(base);
      // Mark main column numeric only if numbers enabled AND we're not extracting inner columns for this base
      const mainIsNumeric = !!(numbersConfig && numbersConfig[base]) && !(needExtract(base) && innerList.length > 0);
      columnIsNumeric.push(mainIsNumeric);
      fromNumbersExtraction.push(mainIsNumeric);
      // If extraction enabled, add inner titles as separate columns WITHOUT base prefix
      innerList.forEach((it) => {
        headers.push(it);
        const isNum = !!(numbersConfig && numbersConfig[base]);
        columnIsNumeric.push(isNum);
        fromNumbersExtraction.push(isNum);
      });
    });
  }
  // If including statistics, prepend a first column for the statistics label
  if (includeStatistics) {
    headers.unshift('statistic');
    columnIsNumeric.unshift(false);
    fromNumbersExtraction.unshift(false);
    if (birthDateColumnIndex > 0) birthDateColumnIndex += 1;
    if (ageColumnIndex > 0) ageColumnIndex += 1;
  }
  worksheet.addRow(headers);
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };

  const parseNumericValue = (raw) => {
    if (raw === null || raw === undefined) return null;
    let s = String(raw).trim();
    if (!s) return null;
    // Remove all non-digit separators except . , -
    s = s.replace(/[^0-9,.-]+/g, '');
    if (!s) return null;
    // Decide decimal separator
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma > -1 && lastDot > -1) {
      // Both present: last one is decimal, remove the other
      if (lastComma > lastDot) {
        // comma decimal
        s = s.replace(/\./g, '');
        s = s.replace(/,/g, '.');
      } else {
        // dot decimal
        s = s.replace(/,/g, '');
      }
    } else if (lastComma > -1) {
      // Only comma: treat as decimal
      s = s.replace(/,/g, '.');
    } else {
      // Only dot or none: dot is decimal; remove leftover thousands if any (none allowed now)
    }
    const num = parseFloat(s);
    return Number.isFinite(num) ? num : null;
  };

  const extractOldestBirthDate = (text, reportDate) => {
    if (!text) return null;
    const results = [];
    const pattern = /(\b\d{1,2})[./](\d{1,2})[./](\d{2,4})\b/g;
    let m;
    while ((m = pattern.exec(text)) !== null) {
      const d = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10);
      let y = m[3];
      if (!d || !mo || d > 31 || mo > 12) continue;
      if (y.length === 2) {
        const yy = parseInt(y, 10);
        const currentYY = new Date().getFullYear() % 100;
        y = (yy > currentYY ? 1900 + yy : 2000 + yy);
      } else {
        y = parseInt(y, 10);
      }
      if (!y || y < 1900 || y > new Date().getFullYear()) continue;
      const dateObj = new Date(y, mo - 1, d);
      if (!isNaN(dateObj.getTime())) results.push(dateObj);
    }
    if (results.length === 0) return null;
    // Oldest
    results.sort((a, b) => a - b);
    const oldest = results[0];
    // If same date as report date (same day), ignore
    if (reportDate instanceof Date && !isNaN(reportDate.getTime())) {
      const sameDay = oldest.getFullYear() === reportDate.getFullYear() && oldest.getMonth() === reportDate.getMonth() && oldest.getDate() === reportDate.getDate();
      if (sameDay) { return null; }
    }
    return oldest;
  };

  const computeAgeYears = (dob, atDate) => {
    if (!(dob instanceof Date) || isNaN(dob) || !(atDate instanceof Date) || isNaN(atDate)) return null;
    let age = atDate.getFullYear() - dob.getFullYear();
    const m = atDate.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && atDate.getDate() < dob.getDate())) {
      age--;
    }
    return age >= 0 ? age : null;
  };

  // Track per-column numeric values for statistics (only for numeric-extraction-driven columns)
  const numericColumnsValues = new Map(); // key: 1-based column index -> number[]

  for (const report of reports) {
    const createdAt = report?.createdAt ? new Date(report.createdAt) : null;
    const dateStr = createdAt ? createdAt.toLocaleString('fr-FR') : '';
    const template = report?.reportType || '';

    const content = getReportContent(report) || '';
    const sections = fullTextMode ? [] : parseReportSections(content);

    // Aggregate content by title (merge duplicates if any)
    const titleToContent = new Map();
    for (const sec of sections) {
      const title = (sec?.title || '').trim();
      const value = (sec?.content || '').trim();
      if (!title) continue;
      if (!titleToContent.has(title)) {
        titleToContent.set(title, value);
      } else {
        const prev = titleToContent.get(title) || '';
        const merged = prev && value ? `${prev}\n\n${value}` : (prev || value);
        titleToContent.set(title, merged);
      }
    }

    const row = [dateStr, template];
    // Collect stats across all numeric-extracted values when requested
    const statsNumbers = includeStatistics ? [] : null;

    if (fullTextMode) {
      // Insert birth date column if requested before content
      if (extractBirthDate) {
        const dob = extractOldestBirthDate(content, createdAt);
        row.push(dob instanceof Date ? dob : '');
        const age = dob instanceof Date ? computeAgeYears(dob, createdAt) : null;
        row.push(typeof age === 'number' ? age : '');
      }
      row.push(content);
    } else {
      const needExtract = (title) => !!extractConfig && !!extractConfig[title];
      const innerCache = {};
      // Insert birth date column if requested before sections
      if (extractBirthDate) {
        const dob = extractOldestBirthDate(content, createdAt);
        row.push(dob instanceof Date ? dob : '');
        const age = dob instanceof Date ? computeAgeYears(dob, createdAt) : null;
        row.push(typeof age === 'number' ? age : '');
      }
      for (const base of selectedSectionTitles) {
        const baseContentVal = titleToContent.get(base) || '';
        const innerList = needExtract(base) ? (expandedInnerByBase[base] || []) : [];
        // Always push main section content
        const numericOnly = !!(numbersConfig && numbersConfig[base]);
        const shouldNumericMain = numericOnly && !(needExtract(base) && innerList.length > 0);
        if (shouldNumericMain) {
          let outVal = baseContentVal;
          const mode = (numbersModeConfig && numbersModeConfig[base]) || 'all';
          if (mode === 'all') {
            const n = parseNumericValue(outVal);
            row.push(n !== null ? n : '');
            if (statsNumbers && n !== null) statsNumbers.push(n);
          } else {
            // For main cell with aggregation, still put single value: min/max/avg from all numbers found in base content
            const nums = Array.from(String(outVal).matchAll(/[-+]?[0-9]*[\.,]?[0-9]+/g)).map(m => parseNumericValue(m[0])).filter(v => typeof v === 'number');
            if (nums.length === 0) row.push('');
            else if (mode === 'min') row.push(Math.min(...nums));
            else if (mode === 'max') row.push(Math.max(...nums));
            else if (mode === 'avg') row.push(nums.reduce((a,b)=>a+b,0)/nums.length);
            else row.push(nums[0]);
            if (statsNumbers && nums.length > 0) statsNumbers.push(...nums);
          }
        } else {
          row.push(baseContentVal);
        }
        if (innerList.length === 0) {
          continue;
        }
        // Build inner map for this base on-demand
        if (!innerCache[base]) {
          const innerSections = parseReportSections(baseContentVal);
          const map = new Map();
          for (const sec of innerSections) {
            const t = (sec?.title || '').trim();
            const v = (sec?.content || '').trim();
            if (!t) continue;
            const prev = map.get(t) || '';
            map.set(t, prev && v ? `${prev}\n\n${v}` : (prev || v));
          }
          innerCache[base] = map;
        }
        innerList.forEach((it) => {
          const v = innerCache[base].get(it) || '';
          if (numericOnly) {
            const mode = (numbersModeConfig && numbersModeConfig[base]) || 'all';
            if (mode === 'all') {
              const n = parseNumericValue(v);
              row.push(n !== null ? n : '');
              if (statsNumbers && n !== null) statsNumbers.push(n);
            } else {
              const nums = Array.from(String(v).matchAll(/[-+]?[0-9]*[\.,]?[0-9]+/g)).map(m => parseNumericValue(m[0])).filter(val => typeof val === 'number');
              if (nums.length === 0) row.push('');
              else if (mode === 'min') row.push(Math.min(...nums));
              else if (mode === 'max') row.push(Math.max(...nums));
              else if (mode === 'avg') row.push(nums.reduce((a,b)=>a+b,0)/nums.length);
              else row.push(nums[0]);
              if (statsNumbers && nums.length > 0) statsNumbers.push(...nums);
            }
          } else {
            row.push(v);
          }
        });
      }
    }
    if (includeStatistics) {
      row.unshift('');
    }
    const added = worksheet.addRow(row);
    // Apply number formatting to numeric columns
    for (let i = 0; i < columnIsNumeric.length; i++) {
      const isNum = columnIsNumeric[i];
      if (!isNum) continue;
      const cell = added.getCell(i + 1);
      if (typeof cell.value === 'number') {
        cell.numFmt = '#,##0.##';
        if (fromNumbersExtraction[i]) {
          if (!numericColumnsValues.has(i + 1)) numericColumnsValues.set(i + 1, []);
          numericColumnsValues.get(i + 1).push(cell.value);
        }
      }
    }
    // Apply date format to birth date column if present
    if (extractBirthDate && birthDateColumnIndex > 0) {
      const cell = added.getCell(birthDateColumnIndex);
      if (cell.value instanceof Date) {
        cell.numFmt = 'dd.mm.yyyy';
      }
    }
  }

  // Insert statistics rows below header, one row per metric, values aligned per numeric extraction columns only
  if (includeStatistics) {
    const allCols = Array.from(numericColumnsValues.keys()).sort((a, b) => a - b);
    const metrics = [
      { name: 'mean', fn: (arr) => arr.reduce((a,b)=>a+b,0)/arr.length },
      { name: 'SD', fn: (arr) => {
        if (arr.length < 2) return 0;
        const m = arr.reduce((a,b)=>a+b,0)/arr.length;
        return Math.sqrt(arr.reduce((s,v)=>s+Math.pow(v-m,2),0)/(arr.length-1));
      }},
      { name: 'median', fn: (arr) => { const s=[...arr].sort((a,b)=>a-b); return s.length%2? s[(s.length-1)/2] : (s[s.length/2-1]+s[s.length/2])/2; }},
      { name: 'IQR 25', fn: (arr) => { const s=[...arr].sort((a,b)=>a-b); const pos=(s.length-1)*0.25; const b=Math.floor(pos); const r=pos-b; return s[b+1]!==undefined? s[b]+r*(s[b+1]-s[b]) : s[b]; }},
      { name: 'IQR 75', fn: (arr) => { const s=[...arr].sort((a,b)=>a-b); const pos=(s.length-1)*0.75; const b=Math.floor(pos); const r=pos-b; return s[b+1]!==undefined? s[b]+r*(s[b+1]-s[b]) : s[b]; }},
    ];
    let insertAt = 2;
    for (const metric of metrics) {
      const rowArr = new Array(headers.length).fill('');
      rowArr[0] = metric.name;
      for (const colIdx of allCols) {
        const arr = numericColumnsValues.get(colIdx) || [];
        if (arr.length > 0) {
          const val = metric.fn(arr);
          rowArr[colIdx - 1] = val;
        }
      }
      worksheet.spliceRows(insertAt, 0, rowArr);
      const added = worksheet.getRow(insertAt);
      for (let j = 0; j < headers.length; j++) {
        const cell = added.getCell(j + 1);
        if (typeof cell.value === 'number') cell.numFmt = '#,##0.##';
      }
      insertAt++;
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}


